import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { MessageSquare, Eye, EyeOff, Save, Send, Loader2, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

const SmsSettings = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testMobile, setTestMobile] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPeId, setShowPeId] = useState(false);

  const [form, setForm] = useState({
    sms_enabled: false,
    sms_api_url: '',
    sms_api_key: '',
    sms_pe_id: '',
    sms_template_id:           '',
    sms_tracking_template_id:  '',
    sms_sender_id: '',
    sms_campaign_id: '',
    sms_route_id: '',
    sms_template_text: '',
    sms_tracking_template_text: '',
    sms_recipient_type: 'all',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${backendUrl}/api/sms/settings`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setForm({
          sms_enabled:      data.sms_enabled === 'true',
          sms_api_url:      data.sms_api_url || '',
          sms_api_key:      data.sms_api_key || '',
          sms_pe_id:        data.sms_pe_id || '',
          sms_template_id:           data.sms_template_id || '',
        sms_tracking_template_id:  data.sms_tracking_template_id || '',
          sms_sender_id:    data.sms_sender_id || '',
          sms_campaign_id:  data.sms_campaign_id || '',
          sms_route_id:     data.sms_route_id || '',
          sms_template_text: data.sms_template_text || '',
          sms_tracking_template_text: data.sms_tracking_template_text || '',
          sms_recipient_type: data.sms_recipient_type || 'all',
        });
      })
      .catch(() => toast.error('Failed to load SMS settings'))
      .finally(() => setLoading(false));
  }, [backendUrl, getHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        sms_enabled: form.sms_enabled ? 'true' : 'false',
      };
      const res = await fetch(`${backendUrl}/api/sms/settings`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('SMS settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testMobile.trim()) { toast.error('Enter a test mobile number'); return; }
    setTestLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/sms/send`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          mobile: testMobile,
          docket_no: 'TEST-001',
          customer_name: 'Test Customer',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Test SMS sent successfully!');
    } catch (e) {
      toast.error(`Test SMS failed: ${e.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="sms-settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-7 h-7 text-text-dark" />
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">SMS Settings</h2>
          <p className="text-sm text-text-secondary font-ibm mt-0.5">Configure SMS gateway for automatic docket notifications</p>
        </div>
      </div>

      {/* Enable Toggle */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-text-dark font-ibm">Enable SMS Notifications</p>
            <p className="text-xs text-text-secondary font-ibm mt-0.5">When enabled, SMS is auto-sent on docket creation and status updates</p>
          </div>
          <Switch
            data-testid="sms-enabled-toggle"
            checked={form.sms_enabled}
            onCheckedChange={v => set('sms_enabled', v)}
          />
        </div>
        {!form.sms_enabled && (
          <div className="mt-3 flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
            <Info className="w-4 h-4 shrink-0" />
            SMS is currently <strong>disabled</strong>. Toggle on to activate.
          </div>
        )}
      </Card>

      {/* Send SMS To */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">Send SMS To</h3>
          <p className="text-xs text-text-secondary font-ibm mt-2">
            Choose which party on the docket receives SMS notifications. SMS is sent to the contact person of the branch selected in the docket form.
            If no contact number is found, a warning is logged with the employee name who created the docket.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'exporter', label: 'Exporter / Shipper only' },
            { value: 'importer', label: 'Importer / Receiver only' },
            { value: 'bill_to',  label: 'Billed To party only' },
            { value: 'all',      label: 'All (Exporter + Importer + Billed To)' },
          ].map(opt => (
            <label
              key={opt.value}
              data-testid={`sms-recipient-${opt.value}`}
              className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-all select-none
                ${form.sms_recipient_type === opt.value
                  ? 'border-accent-blue bg-blue-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
            >
              <input
                type="radio"
                name="sms_recipient_type"
                value={opt.value}
                checked={form.sms_recipient_type === opt.value}
                onChange={() => set('sms_recipient_type', opt.value)}
                className="accent-blue-600 w-4 h-4 shrink-0"
              />
              <span className={`text-sm font-ibm ${form.sms_recipient_type === opt.value ? 'text-accent-blue font-semibold' : 'text-text-dark'}`}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            SMS is sent to the phone number(s) of the <strong>branch selected</strong> in the docket form for each party.
            If no contact number exists, the system logs: <em>&quot;SMS not sent as contact details are not added&quot;</em> along with the employee who created the docket.
          </span>
        </div>
      </Card>

      {/* API Configuration */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-4">
        <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">API Configuration</h3>

        <div className="space-y-1.5">
          <Label className="text-text-dark font-ibm font-medium text-sm">API URL</Label>
          <Input
            data-testid="sms-api-url"
            placeholder="https://kutility.org/app/smsapi/index.php"
            value={form.sms_api_url}
            onChange={e => set('sms_api_url', e.target.value)}
            className="font-mono text-sm border-gray-200"
          />
          <p className="text-xs text-text-secondary font-ibm">Base URL of the SMS gateway (without query parameters)</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* API Key */}
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">API Key <span className="text-xs text-green-600 font-ibm ml-1">(encrypted)</span></Label>
            <div className="relative">
              <Input
                data-testid="sms-api-key"
                type={showApiKey ? 'text' : 'password'}
                placeholder={form.sms_api_key === '***' ? 'Saved (hidden)' : 'Enter API key'}
                value={form.sms_api_key === '***' ? '' : form.sms_api_key}
                onChange={e => set('sms_api_key', e.target.value)}
                className="pr-10 font-mono text-sm border-gray-200"
              />
              <button type="button" onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {form.sms_api_key === '***' && (
              <p className="text-xs text-green-600 font-ibm">Key is saved. Leave blank to keep existing.</p>
            )}
          </div>

          {/* PE ID */}
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">PE ID <span className="text-xs text-green-600 font-ibm ml-1">(encrypted)</span></Label>
            <div className="relative">
              <Input
                data-testid="sms-pe-id"
                type={showPeId ? 'text' : 'password'}
                placeholder={form.sms_pe_id === '***' ? 'Saved (hidden)' : 'Enter PE ID'}
                value={form.sms_pe_id === '***' ? '' : form.sms_pe_id}
                onChange={e => set('sms_pe_id', e.target.value)}
                className="pr-10 font-mono text-sm border-gray-200"
              />
              <button type="button" onClick={() => setShowPeId(!showPeId)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
                {showPeId ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {form.sms_pe_id === '***' && (
              <p className="text-xs text-green-600 font-ibm">PE ID is saved. Leave blank to keep existing.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">Template ID <span className="text-xs text-text-secondary font-ibm">(docket creation)</span></Label>
            <Input data-testid="sms-template-id" placeholder="17071751xxxxxx25232"
              value={form.sms_template_id} onChange={e => set('sms_template_id', e.target.value)}
              className="font-mono text-sm border-gray-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">Tracking Template ID <span className="text-xs text-text-secondary font-ibm">(status update)</span></Label>
            <Input data-testid="sms-tracking-template-id" placeholder="170717xxxxxx69000"
              value={form.sms_tracking_template_id} onChange={e => set('sms_tracking_template_id', e.target.value)}
              className="font-mono text-sm border-gray-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">Sender ID</Label>
            <Input data-testid="sms-sender-id" placeholder="STAVAN"
              value={form.sms_sender_id} onChange={e => set('sms_sender_id', e.target.value)}
              className="font-mono text-sm border-gray-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">Campaign ID</Label>
            <Input data-testid="sms-campaign-id" placeholder="14783"
              value={form.sms_campaign_id} onChange={e => set('sms_campaign_id', e.target.value)}
              className="font-mono text-sm border-gray-200" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">Route ID</Label>
            <Input data-testid="sms-route-id" placeholder="7"
              value={form.sms_route_id} onChange={e => set('sms_route_id', e.target.value)}
              className="font-mono text-sm border-gray-200" />
          </div>
        </div>
      </Card>

      {/* Docket Creation SMS Template */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-3">
        <div>
          <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">Docket Creation SMS Template</h3>
          <p className="text-xs text-text-secondary font-ibm mt-1">Sent automatically when a new docket is created (to exporter &amp; importer)</p>
        </div>
        <div className="flex gap-2 flex-wrap text-xs font-ibm text-text-secondary">
          <span>Placeholders:</span>
          {['{customer_name}', '{docket_no}'].map(p => (
            <code key={p} className="bg-gray-100 px-1.5 py-0.5 rounded text-text-dark font-mono">{p}</code>
          ))}
        </div>
        <Textarea
          data-testid="sms-template-text"
          rows={4}
          value={form.sms_template_text}
          onChange={e => set('sms_template_text', e.target.value)}
          className="font-ibm text-sm border-gray-200 resize-none"
          placeholder="Dear {customer_name}, your docket {docket_no} has been generated..."
        />
        <p className="text-xs text-text-secondary font-ibm">
          Characters: {form.sms_template_text.length} / 160
          {form.sms_template_text.length > 160 && <span className="text-red-500 ml-1">(multiple SMS)</span>}
        </p>
      </Card>

      {/* Tracking Update SMS Template */}
      <Card className="bg-white border border-gray-100 shadow-sm p-5 space-y-3">
        <div>
          <h3 className="font-semibold text-text-dark font-ibm border-b border-gray-100 pb-2">Tracking Update SMS Template</h3>
          <p className="text-xs text-text-secondary font-ibm mt-1">Sent automatically when docket status is updated (to exporter &amp; importer)</p>
        </div>
        <div className="flex gap-2 flex-wrap text-xs font-ibm text-text-secondary">
          <span>Placeholders:</span>
          {['{customer_name}', '{docket_no}', '{status}'].map(p => (
            <code key={p} className="bg-gray-100 px-1.5 py-0.5 rounded text-text-dark font-mono">{p}</code>
          ))}
        </div>
        <Textarea
          data-testid="sms-tracking-template-text"
          rows={4}
          value={form.sms_tracking_template_text}
          onChange={e => set('sms_tracking_template_text', e.target.value)}
          className="font-ibm text-sm border-gray-200 resize-none"
          placeholder="Dear {customer_name}, your docket {docket_no} status: {status}..."
        />
        <p className="text-xs text-text-secondary font-ibm">
          Characters: {form.sms_tracking_template_text.length} / 160
          {form.sms_tracking_template_text.length > 160 && <span className="text-red-500 ml-1">(multiple SMS)</span>}
        </p>
      </Card>

      {/* Save */}
      <Button data-testid="sms-save-btn" onClick={handleSave} disabled={saving}
        className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm px-8">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save Settings
      </Button>

      {/* Test SMS */}
      <Card className="bg-blue-50 border border-blue-200 shadow-sm p-5 space-y-3">
        <h3 className="font-semibold text-blue-800 font-ibm">Test SMS</h3>
        <p className="text-xs text-blue-700 font-ibm">Send a test message using the current template and saved API credentials.</p>
        <div className="flex gap-3">
          <Input
            data-testid="sms-test-mobile"
            placeholder="Enter mobile number (e.g. 9876543210)"
            value={testMobile}
            onChange={e => setTestMobile(e.target.value)}
            className="font-ibm border-blue-200 bg-white"
          />
          <Button data-testid="sms-test-send-btn" onClick={handleTest} disabled={testLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-ibm shrink-0">
            {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Test
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SmsSettings;
